import { PublicClientApplication } from '@azure/msal-browser';
import { useEffect, useState } from 'react';
import Observer from '../../modules/security/observer.util';

const publicClientApplication = new PublicClientApplication({
    auth: {
        clientId: window.REACT_APP_AAD_CLIENT_ID,
        redirectUri: window.REACT_APP_AAD_LOGIN_ACTION_REDIRECT,
        authority: `https://login.microsoftonline.com/${window.REACT_APP_AAD_TENANT_ID}`,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
    },
});

/**
 * Función para cargar los datos inicial de acceso al sistema
 */
export const AuthService = {
    observer: new Observer(),
    state: {},
    setState: (changes) => {
        let newState = { ...AuthService.state, ...changes };

        // validates internal changes
        for (let key of Object.keys(newState)) {
            if (AuthService.state[key] !== newState[key]) {
                AuthService.state = newState;
                AuthService.observer.trigger(AuthService.state);

                return AuthService.state;
            }
        }

        return AuthService.state;
    },
    login: () => {
        AuthService.setState({ authenticating: true });
        return new Promise((resolve, reject) => {
            publicClientApplication
                .handleRedirectPromise()
                .then((authResult) => {
                    console.log('auth result', authResult);
                    // Check if user signed in
                    const account = publicClientApplication.getAllAccounts()[0];
                    if (!account) {
                        console.log('not accounts');
                        resolve(
                            AuthService.setState({
                                authenticating: false,
                                authenticated: false,
                            }),
                        );
                    } else {
                        resolve(
                            AuthService.setState({
                                authenticating: false,
                                authenticated: true,
                            }),
                        );
                    }
                })
                .catch((error) => {
                    console.log(error);
                    reject(
                        AuthService.setState({
                            authenticating: false,
                            authenticated: false,
                            error,
                        }),
                    );
                });
            return publicClientApplication
                .loginPopup({
                    scopes: ['user.read'],
                    prompt: 'select_account',
                })
                ?.then(() => {
                    resolve(
                        AuthService.setState({
                            authenticating: false,
                            authenticated: true,
                        }),
                    );
                    console.log(AuthService.state);
                })
                ?.catch((error) => {
                    reject(
                        AuthService.setState({
                            authenticating: false,
                            authenticated: false,
                            error,
                        }),
                    );
                });
        });
    },
    init: () => {
        AuthService.setState({
            authenticated:
                publicClientApplication.getAllAccounts().length && sessionStorage.getItem('user')
                    ? true
                    : false,
            authenticating: false,
        });
    },
};

/**
 * Función para iniciar sesión, muestra formulario de acceso
 * @returns 
 */
export function login() {
    return AuthService.login();
}

/**
 * Función para salir de la sesión
 */
export function logout() {
    publicClientApplication
        .logoutRedirect({
            postLogoutRedirectUri: window.REACT_APP_AAD_LOGIN_ACTION_REDIRECT,
        })
        .catch((error) => console.error(error));
}

/**
 *  Obtener un token de acceso para un usuario 
 * 
 * @param {*} claims 
 * @param {array} scopes  arreglo de accesos
 * @returns {string} token 
 */
export const getAccessToken = async (claims, scopes = ['user.read']) => {
    try {
        const accounts = publicClientApplication.getAllAccounts();

        if (accounts.length <= 0) {
            console.log('login required');
            throw new Error('login_required');
        }
        // Get the access token silently
        // If the cache contains a non-expired token, this function
        // will just return the cached token. Otherwise, it will
        // make a request to the Azure OAuth endpoint to get a token
        return await publicClientApplication.acquireTokenSilent({
            scopes: scopes,
            account: accounts[0],
        });
    } catch (err) {
        // If a silent request fails, it may be because the user needs
        // to login or grant consent to one or more of the requested scopes
        if (isInteractionRequired(err)) {
            const interactiveResult = await publicClientApplication.acquireTokenPopup({
                scopes: scopes,
            });

            return interactiveResult.accessToken;
        } else {
            throw err;
        }
    }
};

/**
 * Obtener un token de acceso utilizando uno ya disponible o adquiriendo uno nuevo  
 * @returns {string } token de acceso
 */
export const refreshAccessTokenAzure = async () => {
    const account = publicClientApplication.getAllAccounts()[0];
    try {
        const token = await publicClientApplication.acquireTokenSilent({
            account,
            scopes: ['user.read'],
        });
        return token;
    } catch (error) {
        return publicClientApplication
            .acquireTokenPopup({
                account,
                scopes: ['user.read'],
            })
            .then((tokenResp) => {
                return tokenResp;
            })
            .catch((err) => {
                console.log(err);
                return false;
            });
    }
};

/**
 * Obtener el estado de la autenticación
 * 
 * @returns {object} iformación del estado de la autenticación
 */
export function authState() {
    const [state, setState] = useState(AuthService.state);

    useEffect(() => {
        const id = AuthService.observer.subscribe((newState) => setState(newState));

        return () => AuthService.observer.unsubscribe(id);
    }, []);
    return state;
}

/**
 * Función que obtiene los mensaje de error.
 * @param {object} error objeto con la información del error 
 * @returns {string} mensaje de erroro false si no se han producido errores
 */
const isInteractionRequired = (error) => {
    if (!error.message || error.message.length <= 0) {
        return false;
    }

    return (
        error.message.indexOf('consent_required') > -1 ||
        error.message.indexOf('interaction_required') > -1 ||
        error.message.indexOf('login_required') > -1 ||
        error.message.indexOf('no_account_in_silent_request') > -1
    );
};
